/**
 * Page history in the console: which page's history is open, and the page
 * each mounted markdown editor offers it for.
 *
 * The request is module state rather than route state because it is opened
 * from places that never meet the page through props -- the sidebar's View
 * History (through `WebCollabHost.openArtifact`) and the breadcrumb's Document
 * actions -- and the dialog renders beside the editor that can serve it.
 *
 * Data path: `CollabHistoryClient` against the page's DocumentRoom
 * (`{sync}/sync/{room}/revisions`), authorized by the same team JWT callback
 * the page's socket uses. Restore writes through the live editor. The client,
 * recorder and dialog are `loadPageHistory()`, fetched once a page mounts so it
 * can record its revisions (`startWebDocumentHistoryRecording`).
 */
import { useSyncExternalStore } from 'react';
import type { CollabHistoryController, loadPageHistory } from '@nimbalyst/collab-bundle/docs-ui';
import type { CollabEditorHandle, TeamRoomSource } from '@nimbalyst/collab-bundle/editor';

/** A mounted page that can show its history. */
export interface WebDocumentHistoryPage {
  source: TeamRoomSource;
  handle: CollabEditorHandle;
}

type PageHistoryModule = Awaited<ReturnType<typeof loadPageHistory>>;
type HistoryClientClass = PageHistoryModule['CollabHistoryClient'];

let requested: string | null = null;
const pages = new Map<string, WebDocumentHistoryPage>();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function openWebDocumentHistory(documentId: string): void {
  requested = documentId;
  emit();
}

export function closeWebDocumentHistory(): void {
  if (requested === null) return;
  requested = null;
  emit();
}

export function getRequestedWebDocumentHistory(): string | null {
  return requested;
}

export function useRequestedWebDocumentHistory(): string | null {
  return useSyncExternalStore(subscribe, getRequestedWebDocumentHistory);
}

/**
 * The room a typed page's body or a type page's prose lives in, which is the
 * id its editor mount registers history under. Null for any other page.
 */
export function pageHistoryDocumentId(page: { itemId: string | null; typeId: string | null }): string | null {
  if (page.itemId) return `tracker-content/${page.itemId}`;
  if (page.typeId) return `type-page:${page.typeId}`;
  return null;
}

/** Published by a mounted page once its editor exists; cleared when it unmounts. */
export function registerWebDocumentHistory(documentId: string, page: WebDocumentHistoryPage): () => void {
  pages.set(documentId, page);
  emit();
  return () => {
    if (pages.get(documentId) !== page) return;
    pages.delete(documentId);
    emit();
  };
}

/** Whether the page can show its history right now: it is mounted with an editor. */
export function useWebDocumentHistoryAvailable(documentId: string | null | undefined): boolean {
  return useSyncExternalStore(subscribe, () => (documentId ? pages.has(documentId) : false));
}

export function useWebDocumentHistoryPage(documentId: string): WebDocumentHistoryPage | null {
  return useSyncExternalStore(subscribe, () => pages.get(documentId) ?? null);
}

/**
 * The browser twin of the desktop's markdown tab controller: snapshots are the
 * page's markdown, and a restore replaces the body through the live editor.
 */
export function createWebDocumentHistoryController(
  { source, handle }: WebDocumentHistoryPage,
  HistoryClient: HistoryClientClass,
): CollabHistoryController {
  const encoder = new TextEncoder();
  const decoder = new TextDecoder();
  return {
    client: new HistoryClient({
      serverUrl: source.serverUrl,
      // The page's own team-scoped callback; the brands differ only because
      // the editor and docs-ui declarations are generated separately.
      getJwt: () => source.auth.getTeamJwt() as never,
      orgId: source.room.orgId,
      documentId: source.room.documentId,
    }),
    editorType: 'markdown',
    contentFormat: 'markdown',
    previewKind: 'text',
    exportSnapshot: () => encoder.encode(handle.getMarkdown()),
    applySnapshot: (plaintext) => {
      if (!handle.replaceMarkdown) throw new Error('This editor cannot restore a version');
      handle.replaceMarkdown(decoder.decode(plaintext));
    },
    getBasisSequence: () => handle.getBasisSequence?.() ?? 0,
    getStatus: () => {
      const { connection } = handle.getState();
      return connection === 'local' || connection === 'terminated' ? 'disconnected' : connection;
    },
    waitForPendingWrites: async (timeoutMs) => {
      const result = await handle.flush({ timeoutMs });
      return result.status === 'acknowledged' || result.status === 'not-required';
    },
    isReadOnly: () => handle.getState().readOnly,
  };
}

/**
 * Record a mounted page's revisions until the returned stop is called: the
 * room's bootstrap revision when it has none, then an auto revision once an
 * edit has been idle, only while the page is connected and writable. The
 * server never snapshots a page itself, so without this a page edited only in
 * the console had an empty history. Same recorder as the desktop's.
 */
export function startWebDocumentHistoryRecording(
  page: WebDocumentHistoryPage,
  loadHistory: () => Promise<Pick<PageHistoryModule, 'CollabHistoryClient' | 'startCollabRevisionRecording'>>,
): () => void {
  let stopped = false;
  let stopRecording: (() => void) | null = null;
  loadHistory()
    .then(({ CollabHistoryClient, startCollabRevisionRecording }) => {
      if (stopped) return;
      stopRecording = startCollabRevisionRecording(createWebDocumentHistoryController(page, CollabHistoryClient));
    })
    .catch((error: unknown) => {
      console.warn('[webDocumentHistory] Could not start recording page history', error);
    });
  return () => {
    stopped = true;
    stopRecording?.();
  };
}
